/**
 * Custom Confirm Dialog Service
 *
 * This service provides a reliable confirm dialog implementation
 * that integrates with the PartnerUp design system.
 */

import { createApp, ref } from "vue";
import ConfirmDialog from "../components/Display/ConfirmDialog.vue";

class ConfirmDialogService {
	constructor() {
		this.dialogInstance = null;
		this.mountElement = null;
		this.isInitialized = false;
	}

	init() {
		if (this.isInitialized) return;

		// Create a mount element for the dialog
		this.mountElement = document.createElement("div");
		this.mountElement.id = "confirm-dialog-mount";
		document.body.appendChild(this.mountElement);

		// Create the Vue app instance for the dialog
		const dialogApp = createApp(ConfirmDialog);
		this.dialogInstance = dialogApp.mount(this.mountElement);

		this.isInitialized = true;
	}

	confirm(message, options = {}) {
		if (!this.isInitialized) {
			this.init();
		}

		return this.dialogInstance.show({
			message,
			title: options.title || "Confirm",
			okText: options.okText || "OK",
			cancelText: options.cancelText || "Cancel",
		});
	}

	alert(message, options = {}) {
		if (!this.isInitialized) {
			this.init();
		}

		return this.dialogInstance.show({
			message,
			title: options.title || "Alert",
			okText: options.okText || "OK",
			cancelText: false, // Hide cancel button for alerts
		});
	}
}

// Create and export a singleton instance
export const confirmDialogService = new ConfirmDialogService();

// Export individual functions for convenience
export const confirm = (message, options) => confirmDialogService.confirm(message, options);
export const alert = (message, options) => confirmDialogService.alert(message, options);

export default confirmDialogService;
